import type { ReactNode } from "react";
import { useCallback } from "react";
import { ColumnConfig } from "../../../models/config/ColumnConfig";
import { ColumnMatcher } from "../../../models/ColumnMatcher";
import { DlfMatcher } from "../../../models/matchers/DlfMatcher";
import { MatcherFactory } from "../../../models/matchers/factory/MatcherFactory";
import { RegexFieldMatcher } from "../../../models/matchers/RegexFieldMatcher";
import { HardcodeButton } from "../../../styles/buttons/HardcodeButton";
import { AnchorButton } from "../../buttons/AnchorButton";
import { AdvancedTooltip } from "../../tooltips/AdvancedTooltip";
import { FieldMatcherEditor } from "../FieldMatcherEditor";
import { RegexFieldMatcherEditorWithSuggestions } from "../regex/RegexFieldMatcherEditorWithSuggestions";
import { ColumnRange } from "./ColumnRange";
import {
  TTAnchorButton,
  TTColumnEndLabel,
  TTColumnName,
  TTColumnStartLabel,
  TTColumnSuggestions,
  TTHardcodeButton,
  TTTemplateOcrConfidenceThreshold,
} from "../../tooltips/ConfigurationTooltips";
import "../../../styles/dlfEditing/column/columnEditor.css";
import { SuggestionType } from "../../regex/RegexSuggestions";
import { OcrConfidenceThresholdSliderButton } from "../../buttons/OcrConfidenceThresholdSliderButton";

export { ColumnEditor, Props as ColumnEditorProps };

type Props = {
  onRangeMove?: VoidFunction;
  onRangeChange?: VoidFunction;
  onAnchorChange?: VoidFunction;
  onAnchorReverseChange?: (value: boolean) => void;

  // from FieldMatcherEditorChildProps
  matcher: ColumnMatcher;
  nestedMatcher: DlfMatcher;
  /**
   * Suggested values for Column regexes
   */
  suggestions?: SuggestionType[];
  columnConfig: ColumnConfig;
  update(matcher: ColumnMatcher): void;
  /**
   * Add a button component to the FieldMatcherEditor.
   */
  /**
   * Text to display in FieldMatcherEditor label
   */
  label?: string;
  /**
   * Text color for label in FieldMatcherEditor
   */
  labelColor?: string;
  /**
   * Background color for label in FieldMatcherEditor
   */
  labelBgColor?: string;

  highlightButton?: ReactNode;
  /**
   * onClick handler for FieldMatcherEditor label
   */
  onClickLabel?(): void;
  index?: number;
};

// FIXME: add local ColumnEditorButtons component to DRY this up
const ColumnEditor = (props: Props) => {
  const matcherType = props.nestedMatcher.getDiscriminator();

  const matcher = props.matcher;
  const leftXMove = useCallback(
    (value: number) => {
      matcher.columnSection.leftXPercent = value;
      props.onRangeMove && props.onRangeMove();
    },
    [props.onRangeMove],
  );
  const leftXChange = useCallback(
    (value: number) => {
      matcher.columnSection.leftXPercent = value;
      props.onRangeChange && props.onRangeChange();
    },
    [props.onRangeChange],
  );
  const rightXMove = useCallback(
    (value: number) => {
      matcher.columnSection.rightXPercent = value;
      props.onRangeMove && props.onRangeMove();
    },
    [props.onRangeMove],
  );
  const rightXChange = useCallback(
    (value: number) => {
      matcher.columnSection.rightXPercent = value;
      props.onRangeChange && props.onRangeChange();
    },
    [props.onRangeChange],
  );

  return (
    <div className={"columnEditor"}>
      {props.nestedMatcher.getDiscriminator() === "F_REGEX" && (
        <RegexFieldMatcherEditorWithSuggestions
          key={props.nestedMatcher.getMatcherId() + "NESTED"}
          docFieldConfig={props.columnConfig}
          matcher={props.nestedMatcher as RegexFieldMatcher}
          suggestions={props.suggestions}
          suggestionsTooltip={TTColumnSuggestions}
          labelTooltip={TTColumnName}
          update={(x: RegexFieldMatcher) => {
            // setNestedMatcher(x);
            matcher.matcher = x;
            props.update(matcher);
          }}
          button={
            <>
              <AdvancedTooltip overflow tooltipText={TTHardcodeButton}>
                {(tProps) => (
                  <HardcodeButton
                    tProps={tProps}
                    isDefault={
                      props.nestedMatcher.getDiscriminator() === "F_DEFAULT"
                    }
                    onClick={() => {
                      const newMatcherType =
                        matcherType === "F_DEFAULT" ? "F_REGEX" : "F_DEFAULT";
                      const newMatcher = MatcherFactory.changeDlfMatcher(
                        matcher.matcher.toMatcherData(),
                        newMatcherType,
                        matcher.matcher.threshold,
                      );
                      if (newMatcher.getDiscriminator() === "F_REGEX") {
                        // set .+ as default Regex
                        (newMatcher as RegexFieldMatcher).regex = ".+";
                      }
                      // setMatcherType(newMatcherType);
                      // TODO: move this into effect ?
                      // setNestedMatcher(newMatcher);
                      props.matcher.matcher = newMatcher;
                      props.update(matcher);
                    }}
                  />
                )}
              </AdvancedTooltip>

              {matcherType !== "F_REGEX" ? undefined : (
                <AdvancedTooltip overflow tooltipText={TTAnchorButton}>
                  {(tProps) => (
                    <AnchorButton
                      tProps={tProps}
                      anchor={matcher.anchor}
                      reversed={matcher.anchorReverse}
                      onAnchorChange={props.onAnchorChange}
                      onAnchorReverseChange={props.onAnchorReverseChange}
                    />
                  )}
                </AdvancedTooltip>
              )}
            </>
          }
          label={props.matcher.columnName}
          labelBgColor={props.labelBgColor}
          labelColor={props.labelColor}
          onClickLabel={props.onClickLabel}
        />
      )}
      {props.nestedMatcher.getDiscriminator() === "F_DEFAULT" && (
        <FieldMatcherEditor
          docFieldConfig={props.columnConfig}
          matcher={props.nestedMatcher}
          update={(x) => {
            // setNestedMatcher(x);
            matcher.matcher = x;
            props.update(matcher);
          }}
          button={
            <>
              <AdvancedTooltip overflow tooltipText={TTHardcodeButton}>
                {(tProps) => (
                  <HardcodeButton
                    tProps={tProps}
                    isDefault={matcherType === "F_DEFAULT"}
                    onClick={() => {
                      const newMatcherType =
                        matcherType === "F_DEFAULT" ? "F_REGEX" : "F_DEFAULT";
                      const newMatcher = MatcherFactory.changeDlfMatcher(
                        matcher.matcher.toMatcherData(),
                        newMatcherType,
                        matcher.matcher.threshold,
                      );
                      if (newMatcher.getDiscriminator() === "F_REGEX") {
                        // set .+ as default Regex
                        (newMatcher as RegexFieldMatcher).regex = ".+";
                      }
                      // setMatcherType(newMatcherType);
                      // TODO: move this into effect ?
                      // setNestedMatcher(newMatcher);
                      props.matcher.matcher = newMatcher;
                      props.update(matcher);
                    }}
                  />
                )}
              </AdvancedTooltip>
              {matcherType === "F_REGEX" ? (
                <AdvancedTooltip overflow tooltipText={TTAnchorButton}>
                  {(tProps) => (
                    <AnchorButton
                      tProps={tProps}
                      anchor={matcher.anchor}
                      reversed={matcher.anchorReverse}
                      onAnchorChange={props.onAnchorChange}
                      onAnchorReverseChange={props.onAnchorReverseChange}
                    />
                  )}
                </AdvancedTooltip>
              ) : undefined}
            </>
          }
          label={props.matcher.columnName}
          labelBgColor={props.labelBgColor}
          labelColor={props.labelColor}
          onClickLabel={props.onClickLabel}
        />
      )}

      <div className={"columnRanges"}>
        <ColumnRange
          tooltipText={TTColumnStartLabel}
          // key={'start-' + props.matcher.columnSection.getSectionId()}
          label={"Start"}
          disabled={matcherType !== "F_REGEX"}
          value={matcher.columnSection.leftXPercent}
          labelOnTop
          onChange={leftXChange}
          onMove={leftXMove}
        />
        <ColumnRange
          tooltipText={TTColumnEndLabel}
          // key={'end-' + props.matcher.columnSection.getSectionId()}
          label={"End"}
          disabled={matcherType !== "F_REGEX"}
          value={matcher.columnSection.rightXPercent}
          labelOnTop
          onChange={rightXChange}
          onMove={rightXMove}
        />
      </div>
      <div className={"columnOcrConfidence"}>
        <AdvancedTooltip
          overflow
          tooltipText={TTTemplateOcrConfidenceThreshold}
          possiblePlacements={["bottom-center"]}
        >
          {(tProps) => (
            <>
              <span
                {...tProps}
                data-name={"ocrConfidenceThreshold"}
                style={{ marginRight: "0.5rem" }}
              >
                OCR Confidence Threshold:{" "}
              </span>
              <OcrConfidenceThresholdSliderButton
                default={props.columnConfig.ocrConfidenceThreshold ?? 90}
                threshold={props.matcher.matcher.ocrConfidenceThreshold}
                update={(threshold) => {
                  props.matcher.matcher.ocrConfidenceThreshold = threshold;
                  props.update(props.matcher);
                }}
              />
            </>
          )}
        </AdvancedTooltip>
      </div>
    </div>
  );
};
